import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/App";
import { AppSidebar, SidebarMenuButton } from "./AppSidebar";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  ArrowLeft, Calendar, User, Copy, Check, Loader2, Trash2, RefreshCw,
  Download, Clock, ClipboardList, Heart, Edit3, Mail, Plus, MessageSquare,
  Volume2, Square
} from "lucide-react";
import { format } from "date-fns";
import { jsPDF } from "jspdf";
import BottomNav from "./BottomNav";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const authHeaders = () => {
  const t = localStorage.getItem("session_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const DIAGNOSIS_LABELS = {
  diabetes_primary: { label: "Diabeet Esmane", color: "bg-amber-50 text-amber-700 border border-amber-200" },
  diabetes_followup: { label: "Diabeet Korduv", color: "bg-orange-50 text-orange-700 border border-orange-200" },
  diabetes: { label: "Diabeet", color: "bg-amber-50 text-amber-700 border border-amber-200" },
  thyroid_primary: { label: "Kilpnääre Esmane", color: "bg-violet-50 text-violet-700 border border-violet-200" },
  thyroid_followup: { label: "Kilpnääre Korduv", color: "bg-purple-50 text-purple-700 border border-purple-200" },
  thyroid_nodules: { label: "Sõlmed/Kasvaja", color: "bg-rose-50 text-rose-700 border border-rose-200" },
  thyroid: { label: "Kilpnääre", color: "bg-violet-50 text-violet-700 border border-violet-200" },
  other: { label: "Muu", color: "bg-slate-50 text-slate-600 border border-slate-200" },
};

const FIELD_TRANSLATIONS = {
  kaebused: "KAEBUSED", diabeedi_tuup: "DIABEEDI TÜÜP", hba1c_analyysid: "HbA1c & ANALÜÜSID",
  koduses_veresuhkru_sensori_naeidud: "VERESUHKRU / SENSORI NÄIDUD",
  kodused_veresuhkru_sensori_naeidud: "VERESUHKRU / SENSORI NÄIDUD",
  kaal_kmi: "KAAL / KMI", dieet_toitumine: "DIEET & TOITUMINE", eluviis: "ELUVIIS",
  kodune_ravi: "KODUNE RAVI", silmakontroll: "SILMAKONTROLL",
  viimane_silmade_kontroll: "VIIMANE SILMADE KONTROLL",
  parema_silmapohja_analyys: "PAREMA SILMAPÕHJA ANALÜÜS",
  vasaku_silmapohja_analyys: "VASAKU SILMAPÕHJA ANALÜÜS",
  neerufunktsioon: "NEERUFUNKTSIOON", jalgade_probleemid_ravi: "JALGADE PROBLEEMID & RAVI",
  suedameinfarkt_insult: "SÜDAMEINFARKT / INSULT", kardiovaskulaarne_ravi: "KARDIOVASKULAARNE RAVI",
  muud_seisundid: "MUUD SEISUNDID", ravi_muudatused: "RAVI MUUDATUSED",
  jaergmine_vastuvoett: "JÄRGMINE VASTUVÕTT", kodune_ravi_annused: "KODUNE RAVI & ANNUSED",
  // Diabeet Esmane väljad
  pohjus_ja_kaebused: "PÕHJUS & KAEBUSED", diabeedi_tuup_ja_kestus: "DIABEEDI TÜÜP & KESTUS",
  hba1c_ja_trend: "HbA1c & TREND", veresuhkru_andmed: "VERESUHKRU ANDMED",
  huepogluekeemia: "HÜPOGLÜKEEMIA", kaal_kmi_vererohk: "KAAL / KMI / VERERÕHK",
  dieet_ja_eluviis: "DIEET & ELUVIIS", silmad_retinoskopia: "SILMAD & RETINOSKOOPIA",
  neerufunktsioon_uacr: "NEERUFUNKTSIOON & UACR", jalad_neuropaatia: "JALAD & NEUROPAATIA",
  kardiovaskulaarne: "KARDIOVASKULAARNE", ravi_muudatused_pohjendus: "RAVI MUUDATUSED & PÕHJENDUS",
  suunamised: "SUUNAMISED",
  // Diabeet Korduv väljad
  hba1c_ja_veresuhkur: "HbA1c & VERESUHKUR", ravi_ja_annused: "RAVI & ANNUSED",
  kaal_rr: "KAAL & VERERÕHK", tuesistused: "TÜSISTUSED", plaan: "PLAAN",
  // Kilpnääre Esmane väljad
  diagnoos_ja_kestus: "DIAGNOOS & KESTUS",
  laborianaluusid: "LABORIANALÜÜSID", ultraheli_leid: "ULTRAHELI LEID",
  varasemad_protseduurid: "VARASEMAD PROTSEDUURID", silmad_graves: "SILMAD & GRAVES",
  suunamised_uuringud: "SUUNAMISED & UURINGUD",
  // Kilpnääre Korduv väljad
  laborianaluusid_trend: "LABORIANALÜÜSID & TREND", tuesistused_silmad: "TÜSISTUSED & SILMAD",
  // Kilpnäärme Sõlmed/Kasvaja väljad
  kaebused_suemptomid: "KAEBUSED & SÜMPTOMID", solme_kirjeldus: "SÕLME KIRJELDUS",
  biopsia_tulemus: "BIOPSIA TULEMUS", operatsioon_ravi: "OPERATSIOON & RAVI",
  tureoglobuliin_jaelgimine: "TÜREOGLOBULIIN & JÄLGIMINE", levothyroxin_annustamine: "LEVOTHYROXIN ANNUSTAMINE",
  piltdiagnostika: "PILTDIAGNOSTIKA", riskikategooria: "RISKIKATEGOORIA",
  plaan_ja_jaelgimine: "PLAAN & JÄLGIMINE",
  ultraheli: "ULTRAHELI", solmed_kilpnaarmes_biopsia: "SÕLMED KILPNÄÄRMES & BIOPSIA",
  varasemad_operatsioonid_j131_ravi: "VARASEMAD OPERATSIOONID & J131-RAVI",
  complaints: "KAEBUSED", medical_findings: "MEDITSIINILISED LEIUD",
  investigation_plan: "UURINGUTE PLAAN", therapy_plan: "RAVI PLAAN",
  next_appointment: "JÄRGMINE VASTUVÕTT", muu_arutatu: "MUU ARUTELU",
  kokkuvote: "KOKKUVÕTE"
};

const TEMPLATE_FIELDS = {
  diabetes_primary: ["pohjus_ja_kaebused","diabeedi_tuup_ja_kestus","hba1c_ja_trend","veresuhkru_andmed","huepogluekeemia","kodune_ravi_annused","kaal_kmi_vererohk","dieet_ja_eluviis","silmad_retinoskopia","neerufunktsioon_uacr","jalad_neuropaatia","kardiovaskulaarne","muud_seisundid","ravi_muudatused_pohjendus","suunamised","jaergmine_vastuvoett","muu_arutatu"],
  diabetes_followup: ["kaebused","hba1c_ja_veresuhkur","ravi_ja_annused","kaal_rr","tuesistused","eluviis","plaan","muu_arutatu"],
  diabetes: ["pohjus_ja_kaebused","diabeedi_tuup_ja_kestus","hba1c_ja_trend","veresuhkru_andmed","huepogluekeemia","kodune_ravi_annused","kaal_kmi_vererohk","dieet_ja_eluviis","silmad_retinoskopia","neerufunktsioon_uacr","jalad_neuropaatia","kardiovaskulaarne","muud_seisundid","ravi_muudatused_pohjendus","suunamised","jaergmine_vastuvoett","muu_arutatu"],
  thyroid_primary: ["pohjus_ja_kaebused","diagnoos_ja_kestus","laborianaluusid","ultraheli_leid","kodune_ravi_annused","varasemad_protseduurid","silmad_graves","kardiovaskulaarne","muud_seisundid","ravi_muudatused_pohjendus","suunamised_uuringud","jaergmine_vastuvoett","muu_arutatu"],
  thyroid_followup: ["kaebused","laborianaluusid_trend","ravi_ja_annused","ultraheli","kaal_rr","tuesistused_silmad","plaan","muu_arutatu"],
  thyroid_nodules: ["kaebused_suemptomid","solme_kirjeldus","biopsia_tulemus","laborianaluusid","operatsioon_ravi","tureoglobuliin_jaelgimine","levothyroxin_annustamine","piltdiagnostika","riskikategooria","plaan_ja_jaelgimine","muu_arutatu"],
  thyroid: ["pohjus_ja_kaebused","diagnoos_ja_kestus","laborianaluusid","ultraheli_leid","kodune_ravi_annused","varasemad_protseduurid","silmad_graves","kardiovaskulaarne","muud_seisundid","ravi_muudatused_pohjendus","suunamised_uuringud","jaergmine_vastuvoett","muu_arutatu"],
  other: ["kaebused","kodune_ravi","ravi_muudatused","jaergmine_vastuvoett","muu_arutatu"],
};

const getFieldLabel = (key) => FIELD_TRANSLATIONS[key] || key.replace(/_/g, " ").toUpperCase();

const SOAP_LABELS = { subjective: "S - SUBJEKTIIVNE", objective: "O - OBJEKTIIVNE", assessment: "A - HINNANG", plan: "P - PLAAN", muu_arutatu: "MUU ARUTELU" };
const SOAP_COLORS = { subjective: "text-blue-800 bg-blue-50 border-blue-200", objective: "text-teal-800 bg-teal-50 border-teal-200", assessment: "text-amber-800 bg-amber-50 border-amber-200", plan: "text-violet-800 bg-violet-50 border-violet-200", muu_arutatu: "text-slate-800 bg-slate-50 border-slate-200" };
const HANDOUT_LABELS = { mida_arutasime: "MIDA ARUTASIME", teie_ravimid: "TEIE RAVIMID", mida_teha_kodus: "MIDA TEHA KODUS", millal_arsti_juurde: "MILLAL ARSTI JUURDE", tahtis_meeles_pidada: "TÄHTIS MEELES PIDADA" };

export default function MediVoiceSessionPage() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  const audioUrlRef = useRef(null);
  // SOAP
  const [soapData, setSoapData] = useState(null);
  const [loadingSoap, setLoadingSoap] = useState(false);
  const [showSoap, setShowSoap] = useState(false);
  // Handout
  const [handout, setHandout] = useState(null);
  const [loadingHandout, setLoadingHandout] = useState(false);
  const [showHandout, setShowHandout] = useState(false);
  // Edit mode
  const [editMode, setEditMode] = useState(false);
  const [editingFields, setEditingFields] = useState({});
  const [saving, setSaving] = useState(false);
  // Append follow-up
  const [showAppend, setShowAppend] = useState(false);
  const [appendText, setAppendText] = useState("");
  const [appending, setAppending] = useState(false);

  useEffect(() => {
    fetchSession();
    return () => { if (audioUrlRef.current?.startsWith("blob:")) URL.revokeObjectURL(audioUrlRef.current); };
  }, [sessionId]);

  const fetchSession = async () => {
    try {
      const res = await fetch(`${API}/medivoice/sessions/${sessionId}`, { headers: authHeaders() });
      if (!res.ok) throw new Error("Seanssi ei leitud");
      const data = await res.json();
      setSession(data);
      // Audio
      if (data.audio_file_id) {
        try {
          const audioRes = await fetch(`${API}/medivoice/audio/${data.audio_file_id}`, { headers: authHeaders() });
          if (audioRes.ok) {
            const blob = await audioRes.blob();
            const url = URL.createObjectURL(blob);
            audioUrlRef.current = url;
            setAudioUrl(url);
          }
        } catch { /* */ }
      }
    } catch (e) {
      toast.error(e.message);
      navigate("/medivoice");
    } finally {
      setLoading(false);
    }
  };

  const regenerateSummary = async () => {
    setRegenerating(true);
    try {
      const res = await fetch(`${API}/medivoice/sessions/${sessionId}/generate-summary`, {
        method: "POST", headers: authHeaders()
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail); }
      pollForSummary();
    } catch (e) {
      toast.error(e.message);
      setRegenerating(false);
    }
  };

  const pollForSummary = () => {
    let attempts = 0;
    const poll = async () => {
      attempts++;
      try {
        const res = await fetch(`${API}/medivoice/sessions/${sessionId}`, { headers: authHeaders() });
        const s = await res.json();
        if (s.summary && s.summary_status === "completed") {
          setSession(s);
          setRegenerating(false);
          toast.success("Kokkuvõte valmis!");
          return;
        }
        if (s.summary_status === "error") {
          setRegenerating(false);
          toast.error(s.summary_error || "Genereerimine ebaõnnestus");
          return;
        }
        if (attempts >= 60) { setRegenerating(false); toast.error("Aegus"); return; }
        setTimeout(poll, 3000);
      } catch { if (attempts < 60) setTimeout(poll, 3000); else setRegenerating(false); }
    };
    setTimeout(poll, 3000);
  };

  const generateSoap = async () => {
    setLoadingSoap(true); setShowSoap(true);
    try {
      const res = await fetch(`${API}/medivoice/sessions/${sessionId}/generate-soap`, {
        method: "POST", headers: authHeaders()
      });
      if (!res.ok) throw new Error("SOAP ebaõnnestus");
      const data = await res.json();
      setSoapData(data.soap_summary);
    } catch (e) { toast.error(e.message); setShowSoap(false); }
    finally { setLoadingSoap(false); }
  };

  const generateHandout = async () => {
    setLoadingHandout(true); setShowHandout(true);
    try {
      const res = await fetch(`${API}/medivoice/sessions/${sessionId}/patient-handout`, {
        method: "POST", headers: authHeaders()
      });
      if (!res.ok) throw new Error("Juhend ebaõnnestus");
      const data = await res.json();
      setHandout(data);
    } catch (e) { toast.error(e.message); setShowHandout(false); }
    finally { setLoadingHandout(false); }
  };

  const startEdit = () => { if (session?.summary) { setEditingFields({ ...session.summary }); setEditMode(true); } };
  const cancelEdit = () => { setEditingFields({}); setEditMode(false); };

  const saveEdits = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/medivoice/sessions/${sessionId}/update-summary`, {
        method: "PUT", headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ summary: editingFields })
      });
      if (!res.ok) throw new Error("Salvestamine ebaõnnestus");
      const updated = await res.json();
      setSession(updated); setEditMode(false); setEditingFields({});
      toast.success("Kokkuvõte salvestatud!");
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const appendFollowUp = async () => {
    if (!appendText.trim()) { toast.error("Sisesta tekst"); return; }
    setAppending(true);
    try {
      const res = await fetch(`${API}/medivoice/sessions/${sessionId}/append-transcription`, {
        method: "PUT", headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ transcription: appendText.trim() })
      });
      if (!res.ok) throw new Error("Lisamine ebaõnnestus");
      const data = await res.json();
      setSession(data); setAppendText(""); setShowAppend(false);
      toast.success("Järelkontroll lisatud!");
    } catch (e) { toast.error(e.message); }
    finally { setAppending(false); }
  };

  const copySummary = () => {
    if (!session?.summary) return;
    const dateStr = session.created_at ? format(new Date(session.created_at), "dd.MM.yyyy HH:mm") : "";
    let text = `KOKKUVÕTE\nPatsient: ${session.patient_name} (${session.patient_code})\nKuupäev: ${dateStr}\n\n`;
    Object.entries(session.summary).forEach(([k, v]) => { text += `${getFieldLabel(k)}: ${(v || "Pole mainitud").replace(/\s+/g, " ").trim()}\n`; });
    navigator.clipboard?.writeText(text).then(() => { setCopied(true); toast.success("Kopeeritud!"); setTimeout(() => setCopied(false), 2000); });
  };

  const exportPDF = () => {
    if (!session?.summary) return;
    const doc = new jsPDF();
    const pw = doc.internal.pageSize.getWidth();
    const m = 20;
    const mw = pw - 2 * m;
    let y = 20;
    doc.setFontSize(16); doc.setFont("helvetica", "bold");
    doc.text("MEDITSIINILINE KOKKUVÕTE", pw / 2, y, { align: "center" }); y += 12;
    doc.setFontSize(11); doc.setFont("helvetica", "normal");
    doc.text(`Patsient: ${session.patient_name || ""}`, m, y); y += 7;
    doc.text(`Isikukood: ${session.patient_code || ""}`, m, y); y += 7;
    if (session.created_at) { doc.text(`Kuupäev: ${format(new Date(session.created_at), "dd.MM.yyyy HH:mm")}`, m, y); y += 7; }
    y += 5; doc.line(m, y, pw - m, y); y += 10;
    Object.entries(session.summary).forEach(([k, v]) => {
      if (y > 260) { doc.addPage(); y = 20; }
      doc.setFont("helvetica", "bold"); doc.setFontSize(11);
      doc.text(getFieldLabel(k), m, y); y += 6;
      doc.setFont("helvetica", "normal"); doc.setFontSize(10);
      const lines = doc.splitTextToSize(v || "Pole mainitud", mw);
      doc.text(lines, m, y); y += lines.length * 5 + 8;
    });
    doc.save(`kokkuvote_${(session.patient_name || "").replace(/\s+/g, "_")}_${(session.created_at || "").slice(0, 10)}.pdf`);
    toast.success("PDF eksporditud!");
  };

  const exportSoapPDF = () => {
    if (!soapData) return;
    const doc = new jsPDF();
    const pw = doc.internal.pageSize.getWidth();
    const m = 20;
    const mw = pw - 2 * m;
    let y = 20;
    doc.setFontSize(16); doc.setFont("helvetica", "bold");
    doc.text("SOAP KOKKUVÕTE", pw / 2, y, { align: "center" }); y += 12;
    doc.setFontSize(11); doc.setFont("helvetica", "normal");
    doc.text(`Patsient: ${session?.patient_name || ""}`, m, y); y += 10;
    doc.line(m, y, pw - m, y); y += 10;
    ["subjective", "objective", "assessment", "plan", "muu_arutatu"].forEach(k => {
      if (y > 260) { doc.addPage(); y = 20; }
      doc.setFont("helvetica", "bold"); doc.setFontSize(12);
      doc.text(SOAP_LABELS[k] || k, m, y); y += 7;
      doc.setFont("helvetica", "normal"); doc.setFontSize(10);
      const lines = doc.splitTextToSize(soapData[k] || "Pole mainitud", mw);
      doc.text(lines, m, y); y += lines.length * 5 + 10;
    });
    doc.save(`soap_${(session?.patient_name || "").replace(/\s+/g, "_")}.pdf`);
    toast.success("SOAP PDF eksporditud!");
  };

  const exportHandoutPDF = () => {
    if (!handout?.handout) return;
    const doc = new jsPDF();
    const pw = doc.internal.pageSize.getWidth();
    const m = 20;
    const mw = pw - 2 * m;
    let y = 20;
    doc.setFontSize(16); doc.setFont("helvetica", "bold");
    doc.text("VISIIDI KOKKUVÕTE PATSIENDILE", pw / 2, y, { align: "center" }); y += 12;
    doc.setFontSize(11); doc.setFont("helvetica", "normal");
    doc.text(`Patsient: ${handout.patient_name || ""}`, m, y); y += 7;
    doc.text(`Kuupäev: ${handout.date || ""}`, m, y); y += 10;
    doc.line(m, y, pw - m, y); y += 10;
    Object.entries(handout.handout).forEach(([k, v]) => {
      if (y > 260) { doc.addPage(); y = 20; }
      doc.setFont("helvetica", "bold"); doc.setFontSize(11);
      doc.text(HANDOUT_LABELS[k] || k.replace(/_/g, " ").toUpperCase(), m, y); y += 6;
      doc.setFont("helvetica", "normal"); doc.setFontSize(10);
      const lines = doc.splitTextToSize(v || "Pole arutatud", mw);
      doc.text(lines, m, y); y += lines.length * 5 + 8;
    });
    doc.save(`patsiendi_juhend_${(handout.patient_name || "").replace(/\s+/g, "_")}.pdf`);
    toast.success("PDF eksporditud!");
  };

  const deleteSession = async () => {
    if (!window.confirm("Kas oled kindel, et soovid seansi kustutada?")) return;
    try {
      await fetch(`${API}/medivoice/sessions/${sessionId}`, { method: "DELETE", headers: authHeaders() });
      toast.success("Seanss kustutatud");
      navigate("/medivoice");
    } catch { toast.error("Kustutamine ebaõnnestus"); }
  };

  const formatDuration = (s) => { if (!s) return null; return `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, "0")}`; };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  if (!session) return <div className="p-8 text-slate-500">Seanssi ei leitud</div>;

  const diagnosisInfo = DIAGNOSIS_LABELS[session.diagnosis_type] || DIAGNOSIS_LABELS.other;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="medivoice-session-page">
      <AppSidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <div className="md:ml-64">
        <div className="p-6 md:p-8 max-w-6xl mx-auto">
          {/* Back button */}
          <button onClick={() => navigate("/medivoice")}
            className="inline-flex items-center gap-2 text-slate-500 hover:text-[#0F52BA] transition-colors mb-6 text-sm font-medium"
            data-testid="back-to-medivoice">
            <ArrowLeft className="w-4 h-4" />Tagasi MediVoice
          </button>

          {/* Session Header */}
          <div className="bg-white rounded-xl border border-slate-100 shadow-sm mb-8 p-6">
            <div className="flex flex-col md:flex-row items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-3.5 mb-3">
                  <div className="w-11 h-11 bg-[#0F52BA]/10 rounded-xl flex items-center justify-center">
                    <User className="w-5 h-5 text-[#0F52BA]" />
                  </div>
                  <div>
                    <h1 className="text-xl font-bold text-slate-900">{session.patient_name}</h1>
                    <div className="flex items-center gap-2 mt-0.5">
                      <p className="text-sm text-slate-500 font-mono">{session.patient_code}</p>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${diagnosisInfo.color}`}>{diagnosisInfo.label}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-5 text-sm text-slate-500 mt-3">
                  <span className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" />{session.created_at ? format(new Date(session.created_at), "dd.MM.yyyy HH:mm") : ""}</span>
                  {formatDuration(session.duration_seconds) && <span className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" />{formatDuration(session.duration_seconds)}</span>}
                </div>
                {audioUrl && <audio controls src={audioUrl} className="w-full max-w-md mt-4" data-testid="audio-player" />}
              </div>
              <div className="flex gap-2 flex-wrap">
                <Button variant="outline" size="sm" className="text-teal-600 border-teal-200 hover:bg-teal-50 rounded-full text-xs"
                  onClick={() => setShowAppend(!showAppend)} data-testid="append-followup-btn">
                  <Plus className="w-3.5 h-3.5 mr-1" />Järelkontroll
                </Button>
                <Button variant="outline" size="sm" className="text-red-400 border-red-200 hover:bg-red-50 rounded-full"
                  onClick={deleteSession} data-testid="delete-session-btn">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>

          {/* Main grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Transcription */}
            <Card className="bg-white rounded-xl border border-slate-100 shadow-sm">
              <CardHeader><CardTitle className="text-base font-semibold">Transkriptsioon</CardTitle></CardHeader>
              <CardContent>
                {TEMPLATE_FIELDS[session.diagnosis_type] && (
                  <div className="p-3 rounded-lg bg-slate-50 border border-slate-100" data-testid="template-fields-guide">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
                      {diagnosisInfo.label} — malli punktid
                    </p>
                    <ol className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
                      {TEMPLATE_FIELDS[session.diagnosis_type].map((f, i) => (
                        <li key={f} className="text-sm text-slate-600 flex items-start gap-1.5">
                          <span className="text-xs font-mono text-slate-400 mt-0.5 shrink-0">{i + 1}.</span>
                          {getFieldLabel(f)}
                        </li>
                      ))}
                    </ol>
                  </div>
                )}
                {session.transcription && (
                  <details className="mt-3" data-testid="transcription-details">
                    <summary className="text-xs text-slate-400 cursor-pointer hover:text-slate-600 select-none">
                      Vestluse tekst ({session.transcription.length} tähemärki)
                    </summary>
                    <div className="bg-slate-50 rounded-xl p-4 max-h-[500px] overflow-y-auto mt-2">
                      <p className="text-slate-700 text-sm leading-relaxed whitespace-pre-wrap" data-testid="session-transcription">
                        {session.transcription}
                      </p>
                    </div>
                  </details>
                )}
              </CardContent>
            </Card>

            {/* Summary */}
            <Card className={`bg-white rounded-xl border shadow-sm ${session.summary ? "border-[#0F52BA]/20" : "border-slate-100"}`}>
              <CardHeader className="flex flex-row items-start justify-between">
                <CardTitle className="text-base font-semibold">Kokkuvõte ({diagnosisInfo.label})</CardTitle>
                <div className="flex gap-1.5 flex-wrap justify-end">
                  {session.summary && (
                    <>
                      <Button variant="outline" size="sm" onClick={exportPDF} className="rounded-full text-xs" data-testid="export-pdf-btn">
                        <Download className="w-3.5 h-3.5 mr-1" />PDF
                      </Button>
                      <Button variant="outline" size="sm" onClick={copySummary} className="rounded-full text-xs" data-testid="copy-summary-btn">
                        {copied ? <Check className="w-3.5 h-3.5 mr-1" /> : <Copy className="w-3.5 h-3.5 mr-1" />}
                        {copied ? "Kopeeritud!" : "Kopeeri"}
                      </Button>
                      <Button variant="outline" size="sm" onClick={generateSoap} disabled={loadingSoap}
                        className="text-[#0F52BA] border-[#0F52BA]/20 hover:bg-[#0F52BA]/5 rounded-full text-xs" data-testid="soap-btn">
                        {loadingSoap ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <ClipboardList className="w-3.5 h-3.5 mr-1" />}
                        SOAP
                      </Button>
                      <Button variant="outline" size="sm" onClick={generateHandout} disabled={loadingHandout}
                        className="text-rose-600 border-rose-200 hover:bg-rose-50 rounded-full text-xs" data-testid="handout-btn">
                        {loadingHandout ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Heart className="w-3.5 h-3.5 mr-1" />}
                        Patsiendile
                      </Button>
                    </>
                  )}
                  <Button variant="outline" size="sm" onClick={regenerateSummary}
                    disabled={regenerating || !session.transcription} className="rounded-full text-xs" data-testid="regenerate-btn">
                    {regenerating ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />}
                    {session.summary ? "Uuenda" : "Genereeri"}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {regenerating ? (
                  <div className="flex items-center justify-center py-12 gap-3">
                    <Loader2 className="w-5 h-5 animate-spin text-[#0F52BA]" />
                    <span className="text-sm text-slate-600">Genereerin kokkuvõtet...</span>
                  </div>
                ) : session.summary ? (
                  <div className="space-y-4">
                    {editMode ? Object.entries(editingFields).map(([k, v]) => (
                      <div key={k} className="border-b border-slate-100 pb-3 last:border-0">
                        <label className="text-xs font-bold uppercase tracking-wider text-[#0F52BA] mb-1.5 block">{getFieldLabel(k)}</label>
                        <textarea value={v || ""}
                          onChange={e => setEditingFields(prev => ({ ...prev, [k]: e.target.value }))}
                          className="w-full text-sm border border-slate-200 rounded-lg p-2.5 min-h-[60px] focus:border-[#0F52BA] focus:ring-1 focus:ring-[#0F52BA]/20 outline-none resize-y"
                          rows={Math.max(2, Math.ceil((v || "").length / 60))}
                          data-testid={`edit-field-${k}`} />
                      </div>
                    )) : Object.entries(session.summary).map(([k, v]) => (
                      <div key={k} className="border-b border-slate-100 pb-3 last:border-0">
                        <h4 className="text-xs font-bold uppercase tracking-wider text-[#0F52BA] mb-1.5">{getFieldLabel(k)}</h4>
                        <p className="text-sm text-slate-700 leading-relaxed">{(v || "Pole mainitud").replace(/\s+/g, " ").trim()}</p>
                      </div>
                    ))}
                    <div className="mt-6 pt-4 border-t border-slate-200">
                      {editMode ? (
                        <div className="flex gap-2">
                          <Button onClick={saveEdits} disabled={saving} className="flex-1 bg-[#0F52BA] hover:bg-[#0F52BA]/90 text-white" data-testid="save-edits-btn">
                            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}Salvesta
                          </Button>
                          <Button variant="outline" onClick={cancelEdit} data-testid="cancel-edits-btn">Tühista</Button>
                        </div>
                      ) : (
                        <Button onClick={startEdit} disabled={regenerating} className="w-full bg-violet-600 hover:bg-violet-700 text-white" data-testid="edit-summary-btn">
                          <Edit3 className="w-4 h-4 mr-2" />Muuda kokkuvõtet
                        </Button>
                      )}
                    </div>
                  </div>
                ) : session.summary_status === "error" ? (
                  <div className="text-center py-8">
                    <p className="text-red-500 text-sm mb-2">{session.summary_error || "Genereerimine ebaõnnestus"}</p>
                    <Button variant="outline" size="sm" onClick={regenerateSummary} data-testid="retry-summary-btn">Proovi uuesti</Button>
                  </div>
                ) : (
                  <div className="text-center py-12 text-slate-500">
                    <p>Kokkuvõtet pole veel genereeritud</p>
                    <p className="text-sm mt-1">Kliki &quot;Genereeri&quot; kokkuvõtte loomiseks</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* SOAP */}
          {showSoap && (
            <Card className="bg-white rounded-xl border border-[#0F52BA]/20 shadow-sm mt-8" data-testid="soap-card">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2 text-[#0F52BA]">
                    <ClipboardList className="w-5 h-5" />SOAP Kokkuvõte
                  </CardTitle>
                  <div className="flex gap-2">
                    {soapData && <Button variant="outline" size="sm" onClick={exportSoapPDF} className="text-blue-700 border-blue-300" data-testid="soap-pdf-btn"><Download className="w-4 h-4 mr-1" />PDF</Button>}
                    <Button variant="ghost" size="sm" onClick={() => setShowSoap(false)} className="text-xs text-slate-400">Sulge</Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {loadingSoap ? (
                  <div className="flex items-center justify-center py-8 gap-3"><Loader2 className="w-5 h-5 animate-spin text-blue-600" /><span className="text-sm text-blue-700">Genereerin...</span></div>
                ) : soapData ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {["subjective", "objective", "assessment", "plan", "muu_arutatu"].map(k => (
                      <div key={k} className={`p-4 rounded-lg border ${SOAP_COLORS[k] || ""} ${k === "muu_arutatu" ? "md:col-span-2" : ""}`}>
                        <h4 className="text-xs font-bold mb-2">{SOAP_LABELS[k]}</h4>
                        <p className="text-sm">{soapData[k] || "Pole mainitud"}</p>
                      </div>
                    ))}
                  </div>
                ) : null}
              </CardContent>
            </Card>
          )}

          {/* Patient Handout */}
          {showHandout && (
            <Card className="bg-white rounded-xl border border-rose-200 shadow-sm mt-8" data-testid="handout-card">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2 text-rose-700"><Heart className="w-5 h-5" />Patsiendi juhend</CardTitle>
                  <div className="flex gap-2">
                    {handout?.handout && <Button variant="outline" size="sm" onClick={exportHandoutPDF} className="text-rose-700 border-rose-300" data-testid="handout-pdf-btn"><Download className="w-4 h-4 mr-1" />PDF</Button>}
                    <Button variant="ghost" size="sm" onClick={() => setShowHandout(false)} className="text-xs text-slate-400">Sulge</Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {loadingHandout ? (
                  <div className="flex items-center justify-center py-8 gap-3"><Loader2 className="w-5 h-5 animate-spin text-rose-600" /><span className="text-sm text-rose-700">Genereerin...</span></div>
                ) : handout?.handout ? (
                  <div className="space-y-4">
                    {Object.entries(handout.handout).map(([k, v]) => (
                      <div key={k} className="border-b border-rose-200 pb-3 last:border-0">
                        <h4 className="text-xs font-bold text-rose-800 mb-1">{HANDOUT_LABELS[k] || k.replace(/_/g, " ").toUpperCase()}</h4>
                        <p className="text-sm text-slate-700 whitespace-pre-wrap">{v || "Pole arutatud"}</p>
                      </div>
                    ))}
                    <p className="text-xs text-slate-400 pt-2">See juhend on koostatud AI abil. Palun kontrollige enne patsiendile andmist.</p>
                  </div>
                ) : null}
              </CardContent>
            </Card>
          )}

          {/* Append Follow-up */}
          {showAppend && (
            <Card className="border-teal-300 bg-gradient-to-b from-teal-50 to-white mt-8" data-testid="append-card">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2 text-teal-800">
                    <MessageSquare className="w-5 h-5" />Lisa järelkontroll
                  </CardTitle>
                  <Button variant="ghost" size="sm" className="text-slate-400 text-xs" onClick={() => setShowAppend(false)}>Sulge</Button>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-slate-600 mb-3">
                  Lisa analüüside tulemused, järelkontrolli märkmed vms. Pärast lisamist genereeri kokkuvõte uuesti.
                </p>
                <textarea value={appendText} onChange={e => setAppendText(e.target.value)}
                  placeholder="Nt: Analüüside tulemused: HbA1c 7.2%, kolesterool 5.8..."
                  className="w-full text-sm border border-teal-200 rounded-lg p-3 min-h-[100px] focus:border-teal-400 focus:ring-1 focus:ring-teal-200 outline-none resize-y"
                  data-testid="append-textarea" />
                <Button onClick={appendFollowUp} disabled={appending || !appendText.trim()}
                  className="w-full mt-3 bg-teal-600 hover:bg-teal-700 text-white" data-testid="append-submit-btn">
                  {appending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}
                  Lisa sessioonile
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      <BottomNav />
    </div>
  );
}
